import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircleHeart } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import ListingCard from "../components/ListingCard";

const BEDS_MAP = { studio: 0, "1-bedroom": 1, "2-bedroom": 2, "3-bedroom": 3 };
const BEDS_LABEL = { studio: "Studio", "1-bedroom": "1 bedroom", "2-bedroom": "2 bedroom", "3-bedroom": "3 bedroom" };

export default function Rentals() {
  const { slug } = useParams();
  const [hoods, setHoods] = useState([]);
  const [listings, setListings] = useState(null);

  const parsed = useMemo(() => {
    if (!hoods.length) return null;
    for (const b of Object.keys(BEDS_MAP)) {
      if (slug.startsWith(b + "-")) {
        const h = hoods.find((x) => x.slug === slug.slice(b.length + 1));
        if (h) return { hood: h, beds: b };
      }
    }
    const h = hoods.find((x) => x.slug === slug);
    return h ? { hood: h, beds: null } : null;
  }, [slug, hoods]);

  useEffect(() => { api.get("/neighborhoods").then((r) => setHoods(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!parsed) return;
    const params = { neighborhood: parsed.hood.name };
    if (parsed.beds != null) params.beds = BEDS_MAP[parsed.beds];
    api.get("/listings", { params }).then((r) => setListings(r.data)).catch(() => setListings([]));
  }, [parsed]);

  if (hoods.length && !parsed) {
    return <div className="max-w-2xl mx-auto py-24 text-center"><h1 className="text-2xl font-bold">Page not found</h1><Link to="/listings" className="mt-4 inline-block text-primary underline">Browse all rentals</Link></div>;
  }
  if (!parsed) return <div className="py-24 text-center text-muted-foreground">Loading…</div>;

  const { hood, beds } = parsed;
  const h1 = beds ? `${BEDS_LABEL[beds]} apartments for rent in ${hood.name}` : `Apartments for rent in ${hood.name}`;
  const desc = beds
    ? `Browse ${BEDS_LABEL[beds]} rentals in ${hood.name}, ${hood.city}. Real, de-duplicated listings matched by Tendly's free AI concierge.`
    : `Find long-term rentals in ${hood.name}, ${hood.city}. ${hood.blurb} Free AI concierge matching from Tendly.`;

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 md:py-16">
      <SEO title={`${h1} | Tendly`} description={desc}
        jsonLd={[
          { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "/" },
            { "@type": "ListItem", position: 2, name: `Rentals in ${hood.name}` },
          ]},
          { "@context": "https://schema.org", "@type": "ItemList", name: h1,
            numberOfItems: listings?.length || 0 },
        ]} />
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:text-primary">Home</Link> / <span className="text-foreground">{h1}</span>
      </nav>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{h1}</h1>
      <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
        {hood.blurb} {hood.avg_1br_rent ? `One-bedroom homes here average around $${hood.avg_1br_rent.toLocaleString()}/month right now. ` : ""}
        Every listing below is live inventory aggregated and de-duplicated by Tendly — free for renters, always.
      </p>

      <p data-testid="seo-listings-count" className="mt-8 text-sm text-muted-foreground">{listings ? `${listings.length} homes available` : "Loading…"}</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {(listings || []).map((l) => <ListingCard key={l.listing_id} listing={l} />)}
      </div>
      {listings && !listings.length && (
        <div className="mt-12 rounded-2xl border border-border bg-card p-8 text-center" data-testid="seo-empty">
          <p className="font-semibold">No homes listed here yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">Start a chat and we'll help you find one — new listings arrive as landlords submit them.</p>
          <Link to="/chat" data-testid="seo-open-chat" className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold">
            <MessageCircleHeart className="w-4 h-4" /> Open live chat
          </Link>
        </div>
      )}

      <div className="mt-14 border-t border-border pt-8">
        <h2 className="font-heading font-semibold text-lg">Explore more</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.keys(BEDS_MAP).map((b) => (
            <Link key={b} to={`/rentals/${b}-${hood.slug}`} className="rounded-full border border-border px-4 py-1.5 text-sm hover:border-primary transition-colors">
              {BEDS_LABEL[b]} in {hood.name}
            </Link>
          ))}
          {hoods.filter((h) => h.slug !== hood.slug).slice(0, 8).map((h) => (
            <Link key={h.slug} to={beds ? `/rentals/${beds}-${h.slug}` : `/rentals/${h.slug}`}
              className="rounded-full border border-border px-4 py-1.5 text-sm hover:border-primary transition-colors">
              {beds ? `${BEDS_LABEL[beds]} in ${h.name}` : h.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
